import type { LocalDate } from '@zg-rentals/ts-rental-manager-api-client';
import { LoadingConstants, numberUtils, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { PageWrapper } from '@zg-rentals/rental-revenue-components';
import { GuestPayFooter } from './GuestPayFooter';
import { Redirect, useHistory } from 'react-router-dom';
import { manifest } from '../../app/routes';
import { useAppGuestPay } from '../../hooks/useGuestPay';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormField,
  Input,
  Label,
  Modal,
  Paragraph,
  Table,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import React from 'react';
import type { AdproductsGuestPayZuoraDto } from '@zg-rentals/ts-adproducts-client';
import {
  AdproductsGuestBillingInvoiceApiHandler_saveGuestPayInvoiceSelectionForGuest,
  AdproductsGuestBillingInvoiceApiHandler_sendGuestPaySupportEmail,
  AdproductsUserBillingInvoiceApiHandler_saveGuestPayInvoiceSelection,
  AdproductsUserBillingInvoiceApiHandler_sendGuestPaySupportEmail,
} from '@zg-rentals/ts-adproducts-client';
import {
  emitGuestPaySelectInvoiceCancelEvent,
  emitGuestPaySelectInvoiceContinueEvent,
  emitGuestPaySelectInvoiceEvent,
  emitGuestPaySelectInvoiceHelpModalOpenEvent,
  emitGuestPaySelectInvoiceSubmitHelpEvent,
} from '../../model/GuestPayClickstreamEvents';

export const dateFormat = (date: LocalDate) => `${date.month}/${date.day}/${date.year}`;

export const GuestPaySelectInvoicesPage = () => {
  const [invoiceNumberForHelp, setInvoiceNumberForHelp] = React.useState('');
  const [helpRequestDetails, setHelpRequestDetails] = React.useState('');
  const [submitHelpLoadingState, setSubmitHelpLoadingState] = React.useState<LoadingConstants>(
    LoadingConstants.INITIAL,
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);
  const [showHelpModal, setShowHelpModal] = React.useState(false);

  const history = useHistory();
  const { guestPayState, updateGuestPayState } = useAppGuestPay();
  const { user } = useRentalManagerUser();
  const sortedInvoices = React.useMemo(() => {
    const primaryInvoice = guestPayState.invoices.find(
      (invoice) => invoice.invoiceNumber === guestPayState.primaryInvoice,
    );
    const otherInvoices = guestPayState.invoices.filter(
      (invoice) => invoice.invoiceNumber !== guestPayState.primaryInvoice,
    );
    if (primaryInvoice) {
      otherInvoices.unshift(primaryInvoice);
    }
    return otherInvoices;
  }, [guestPayState.invoices, guestPayState.primaryInvoice]);

  const subTotal = React.useMemo(() => {
    const calculatedSubtotal = guestPayState.invoices
      .filter((invoice) => guestPayState.selectedInvoices.has(invoice.invoiceNumber))
      .map((invoice) => invoice.balance)
      .reduce((a, b) => a + b, 0);
    return calculatedSubtotal;
  }, [guestPayState.invoices, guestPayState.selectedInvoices]);

  const mainAccountName = React.useMemo(() => {
    if (guestPayState.guestPayLoadingSessionState === LoadingConstants.INITIAL) {
      return undefined;
    }
    return guestPayState.invoices[0].accountName;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [guestPayState.invoices]);

  const handleSaveSelection = () => {
    const selectedInvoiceIds = guestPayState.invoices
      .filter((invoice) => guestPayState.selectedInvoices.has(invoice.invoiceNumber))
      .map((invoice) => {
        return invoice.invoiceId;
      });
    const params = {
      guestPaySessionId: guestPayState.guestPaySessionKey,
      selectedInvoiceIds: selectedInvoiceIds,
    };
    let api;
    if (user.loggedIn) {
      api = AdproductsUserBillingInvoiceApiHandler_saveGuestPayInvoiceSelection(params);
    } else {
      api = AdproductsGuestBillingInvoiceApiHandler_saveGuestPayInvoiceSelectionForGuest(params);
    }
    api
      .then(() => {
        emitGuestPaySelectInvoiceContinueEvent({ user });
        history.push(manifest.guestPayPaymentDetailsPage);
        return;
      })
      .catch(() => {
        // todo handle error
      });
  };

  if (!guestPayState.invoices.length) {
    return <Redirect to={manifest.guestPayPage} />;
  }

  const handleSubmitHelp = async () => {
    setSubmitHelpLoadingState(LoadingConstants.LOADING);

    try {
      if (user.loggedIn) {
        await AdproductsUserBillingInvoiceApiHandler_sendGuestPaySupportEmail({
          invoiceNumber: invoiceNumberForHelp,
          message: helpRequestDetails,
          guestPaySessionId: guestPayState.guestPaySessionKey || '',
        });
      } else {
        await AdproductsGuestBillingInvoiceApiHandler_sendGuestPaySupportEmail({
          invoiceNumber: invoiceNumberForHelp,
          message: helpRequestDetails,
          guestPaySessionId: guestPayState.guestPaySessionKey || '',
        });
      }
      emitGuestPaySelectInvoiceSubmitHelpEvent({ user });
      setSubmitHelpLoadingState(LoadingConstants.SUCCESS);
      setShowHelpModal(false);
      setInvoiceNumberForHelp('');
      setHelpRequestDetails('');
    } catch {
      setSubmitHelpLoadingState(LoadingConstants.ERROR);
    }
  };

  const HelpModal = () => (
    <Modal
      open={showHelpModal}
      onOpenChange={(open) => setShowHelpModal(open)}
      size="md"
      header="Invoice inquiries"
      body={
        <Box>
          {submitHelpLoadingState === LoadingConstants.ERROR && (
            <Alert tone="critical">Something went wrong, please try again later.</Alert>
          )}
          <Text css={{ marginBottom: 'md' }}>
            Let us know if you see an invoice you believe was already paid by sending a note here or emailing {` `}
            <a href="mailto:ar@zillowgroup.com" style={{ color: 'blue', textDecoration: 'underline' }}>
              ar@zillowgroup.com
            </a>
            . Make sure to include the invoice number you&apos;re contacting us about. We should respond within 24
            business hours.
          </Text>
          <FormField
            css={{ marginTop: 'sm', marginBottom: 'sm' }}
            label={<Label>Invoice number</Label>}
            control={
              <Input
                disabled={submitHelpLoadingState === LoadingConstants.LOADING}
                value={invoiceNumberForHelp}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInvoiceNumberForHelp(e.target.value)}
              />
            }
          />
          <FormField
            css={{ marginBottom: 'sm' }}
            label={<Label>Details of request</Label>}
            control={
              <Input
                disabled={submitHelpLoadingState === LoadingConstants.LOADING}
                value={helpRequestDetails}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHelpRequestDetails(e.target.value)}
              />
            }
          />
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Modal actions">
          <Button tone="neutral" emphasis="outlined" onClick={() => setShowHelpModal(false)}>
            Cancel
          </Button>
          <Button
            tone="brand"
            emphasis="filled"
            onClick={handleSubmitHelp}
            disabled={submitHelpLoadingState === LoadingConstants.LOADING}
          >
            {submitHelpLoadingState === LoadingConstants.LOADING ? 'Sending...' : 'Send'}
          </Button>
        </ButtonGroup>
      }
    />
  );

  const handleToggleInvoice = (invoice: AdproductsGuestPayZuoraDto) => {
    const newSelectedInvoices = new Set(guestPayState.selectedInvoices);
    if (newSelectedInvoices.has(invoice.invoiceNumber)) {
      newSelectedInvoices.delete(invoice.invoiceNumber);
    } else {
      newSelectedInvoices.add(invoice.invoiceNumber);
    }
    const invoices = sortedInvoices.map((inv) => ({
      ...inv,
      selectedForPayment: newSelectedInvoices.has(inv.invoiceNumber),
    }));
    updateGuestPayState({ invoices, selectedInvoices: newSelectedInvoices });
    emitGuestPaySelectInvoiceEvent({ user });
  };

  const payableInvoices = sortedInvoices.filter((invoice) => invoice.balance > 0);
  const allPayableSelected =
    payableInvoices.length > 0 &&
    payableInvoices.every((invoice) => guestPayState.selectedInvoices.has(invoice.invoiceNumber));
  const somePayableSelected = payableInvoices.some((invoice) =>
    guestPayState.selectedInvoices.has(invoice.invoiceNumber),
  );

  const handleToggleAll = () => {
    const newSelectedInvoices = new Set(guestPayState.selectedInvoices);
    if (allPayableSelected) {
      for (const invoice of payableInvoices) {
        newSelectedInvoices.delete(invoice.invoiceNumber);
      }
    } else {
      for (const invoice of payableInvoices) {
        newSelectedInvoices.add(invoice.invoiceNumber);
      }
    }
    const invoices = sortedInvoices.map((inv) => ({
      ...inv,
      selectedForPayment: newSelectedInvoices.has(inv.invoiceNumber),
    }));
    updateGuestPayState({ invoices, selectedInvoices: newSelectedInvoices });
    emitGuestPaySelectInvoiceEvent({ user });
  };

  const primaryInvoices = sortedInvoices.filter((invoice) => invoice.invoiceNumber === guestPayState.primaryInvoice);
  const secondaryInvoices = sortedInvoices.filter((invoice) => invoice.invoiceNumber !== guestPayState.primaryInvoice);

  const renderInvoiceRow = (invoice: AdproductsGuestPayZuoraDto) => (
    <Table.Row key={invoice.invoiceNumber} css={{ paddingBlock: '20px' }}>
      <Table.Cell>
        <Checkbox
          aria-label={`Select invoice ${invoice.invoiceNumber}`}
          checked={guestPayState.selectedInvoices.has(invoice.invoiceNumber)}
          disabled={!invoice.balance}
          onChange={() => handleToggleInvoice(invoice)}
        />
      </Table.Cell>
      <Table.Cell>{dateFormat(invoice.dueDate)}</Table.Cell>
      <Table.Cell>
        <Text textStyle="body-bold">{invoice.invoiceNumber}</Text>
      </Table.Cell>
      <Table.Cell>{`${dateFormat(invoice.serviceStart)}-${dateFormat(invoice.serviceEnd)}`}</Table.Cell>
      <Table.Cell>
        <Text textStyle="body-bold">{numberUtils.asCurrency(invoice.balance)}</Text>
      </Table.Cell>
    </Table.Row>
  );

  return (
    <PageWrapper title={`Invoices for ${mainAccountName}`} subTitle={`Account number: ${guestPayState.accountNumber}`}>
      <Box css={{ position: 'relative' }} data-testid="guestPayInvoicesTable">
        <Box css={{ position: 'absolute', top: '-30px', right: '0px' }}>
          <Paragraph textStyle="body-bold">
            {guestPayState.selectedInvoices.size} invoice{guestPayState.selectedInvoices.size !== 1 ? 's' : ''} selected
          </Paragraph>
        </Box>
        <Modal
          open={isCancelModalOpen}
          onOpenChange={(open) => setIsCancelModalOpen(open)}
          size="sm"
          header="Exit now?"
          body={<Text>You&apos;ll lose your changes and have to start over from the beginning.</Text>}
          footer={
            <ButtonGroup aria-label="Modal actions">
              <Button tone="neutral" emphasis="outlined" onClick={() => setIsCancelModalOpen(false)}>
                Cancel
              </Button>
              <Button
                tone="brand"
                emphasis="filled"
                onClick={() => {
                  emitGuestPaySelectInvoiceCancelEvent({ user });
                  history.push(manifest.guestPayPage);
                }}
              >
                Exit
              </Button>
            </ButtonGroup>
          }
        />
        <Table.Root
          appearance="horizontal"
          aria-label="Guest pay invoices table"
          alignments={['center', 'center', 'start', 'start', 'start']}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Checkbox
                  aria-label="Select all invoices"
                  checked={allPayableSelected}
                  indeterminate={somePayableSelected && !allPayableSelected}
                  disabled={payableInvoices.length === 0}
                  onChange={handleToggleAll}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>Due date</Table.HeaderCell>
              <Table.HeaderCell>Invoice number</Table.HeaderCell>
              <Table.HeaderCell>Service period</Table.HeaderCell>
              <Table.HeaderCell>Invoice balance</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {primaryInvoices.length > 0 && (
              <>
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Box css={{ marginBlock: 'xs' }}>
                      <Text textStyle="heading-sm">Selected invoice</Text>
                    </Box>
                  </Table.Cell>
                </Table.Row>
                {primaryInvoices.map(renderInvoiceRow)}
              </>
            )}
            {secondaryInvoices.length > 0 && (
              <>
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Box css={{ marginBlock: 'xs' }}>
                      <Paragraph textStyle="body-bold">Other due invoices</Paragraph>
                      <Text>
                        To avoid account suspension or late fees, prioritize paying invoices before their due date. See
                        an invoice that you already paid?{' '}
                        <TextButton
                          onClick={() => {
                            emitGuestPaySelectInvoiceHelpModalOpenEvent({ user });
                            setShowHelpModal(true);
                          }}
                        >
                          Contact us
                        </TextButton>{' '}
                        to resolve it.
                      </Text>
                    </Box>
                  </Table.Cell>
                </Table.Row>
                {secondaryInvoices.map(renderInvoiceRow)}
              </>
            )}
          </Table.Body>
        </Table.Root>
      </Box>
      <GuestPayFooter
        handleBackButton={() => {
          setIsCancelModalOpen(true);
        }}
        handleNext={handleSaveSelection}
        subTotal={subTotal}
        isNextDisabled={guestPayState.selectedInvoices.size === 0}
      />
      <HelpModal />
    </PageWrapper>
  );
};
